import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MenuSVG } from "./SVG";

// Fix for default marker icons in Leaflet
import icon from "leaflet/dist/images/marker-icon.png";

let DefaultIcon = L.icon({
  iconUrl: icon,
});

L.Marker.prototype.options.icon = DefaultIcon;

// Map style options
const mapStyles = {
  default: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  light: {
    url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  dark: {
    url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
  },
  streets: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012",
  },
};

const EventsMap = ({
  events,
  mapStyle = "default",
  isEditable = false,
  onStyleChange,
  highlightColor,
}) => {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const [currentStyle, setCurrentStyle] = useState(mapStyle);
  const [showOptions, setShowOptions] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ left: 0, top: 0 });
  const optionsRef = useRef(null);
  const buttonRef = useRef(null);

  console.log(highlightColor);
  useEffect(() => {
    setCurrentStyle(mapStyle);
  }, [mapStyle]);

  useEffect(() => {
    // Filter events with coordinates
    const validEvents = events.filter(
      (event) =>
        event.coordinates && event.coordinates.lat && event.coordinates.lng,
    );

    if (validEvents.length === 0) return;

    // Initialize map if it doesn't exist
    if (!leafletMap.current) {
      leafletMap.current = L.map(mapRef.current).setView(
        [45.815399, 15.966568],
        12,
      );

      // Get the selected map style or fallback to default
      const selectedStyle = mapStyles[currentStyle] || mapStyles.default;

      L.tileLayer(selectedStyle.url, {
        // attribution: selectedStyle.attribution,
      }).addTo(leafletMap.current);
    } else {
      // Update tile layer if map already exists and style changes
      leafletMap.current.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          leafletMap.current.removeLayer(layer);
        }
      });

      const selectedStyle = mapStyles[currentStyle] || mapStyles.default;
      L.tileLayer(selectedStyle.url, {
        // attribution: selectedStyle.attribution,
      }).addTo(leafletMap.current);
    }

    // Clear existing markers
    leafletMap.current.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        leafletMap.current.removeLayer(layer);
      }
    });

    const markers = [];
    validEvents.forEach((event, index) => {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        event.location,
      )}`;

      // Create a custom numbered icon
      const customIcon = L.divIcon({
        className: "custom-numbered-marker",
        html: `<div style="background-color: ${highlightColor}; color: white; border-radius: 50%; width: 26px; height: 26px; display: flex; justify-content: center; align-items: center; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${
          index + 1
        }</div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -13],
      });

      const marker = L.marker([event.coordinates.lat, event.coordinates.lng], {
        icon: customIcon,
      })
        .addTo(leafletMap.current)
        .bindPopup(
          `<b>${event.eventName}</b><br>${event.time}<br><a href="${googleMapsUrl}" target="_blank" rel="noopener noreferrer" style="color: #14b8a6; text-decoration: underline;">${event.location}</a>`,
        );

      markers.push(marker);
    });

    // Fit map to show all markers
    if (markers.length > 0) {
      const group = new L.featureGroup(markers);
      leafletMap.current.fitBounds(group.getBounds().pad(0.1));
    }

    // Cleanup on unmount
    return () => {
      if (leafletMap.current) {
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
  }, [events, currentStyle, highlightColor]);

  const handleStyleChange = (style) => {
    setCurrentStyle(style);
    if (onStyleChange) {
      onStyleChange(style);
    }
    setShowOptions(false);
  };

  const handleButtonClick = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setOptionsPosition({
      top: rect.bottom + 10,
      left: window.innerWidth / 2,
    });

    setShowOptions((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showOptions &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptions]);

  return (
    <div className="section p-2 mb-4 group relative">
      <div
        ref={mapRef}
        style={{ height: "400px", width: "100%", borderRadius: "8px" }}
        className={`${
          isEditable ? "border-2 border-dashed border-gray-200" : ""
        }`}
      ></div>

      {isEditable && (
        <button
          type="button"
          ref={buttonRef}
          onClick={handleButtonClick}
          className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 rounded-full z-[999]"
        >
          <MenuSVG />
        </button>
      )}

      {showOptions && (
        <div
          ref={optionsRef}
          className="fixed bg-white   rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-[999] transform -translate-x-1/2 p-2"
          style={{
            left: `${optionsPosition.left}px`,
            top: `${optionsPosition.top}px`,
          }}
        >
          <div className="grid grid-cols-3 gap-2">
            {Object.keys(mapStyles).map((style) => (
              <button
                key={style}
                onClick={() => handleStyleChange(style)}
                className={`text-xs py-1 px-2 rounded transition-colors ${
                  currentStyle === style
                    ? "bg-teal-500 text-white"
                    : "bg-white   text-gray-700   hover:bg-gray-100  :bg-gray-600"
                }`}
              >
                {style.charAt(0).toUpperCase() + style.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsMap;
